import React, { useState, useCallback } from 'react';
import { TabbedCard } from '../components/Ui/Card.jsx';
import { TabController } from '../components/TabController/TabController.jsx';
import { TabPanel } from '../components/TabController/TabPanel.jsx';
import { TabContent } from '../components/StyledComponents/Tabs/Tabs.jsx';
import { CenterContainer, FormContainer } from '../components/StyledComponents/Forms/Forms.jsx';
import { fetchOpticiansClinics } from '../http/opticiansClinics.js';
import { fetchManagedClinics } from '../http/managedClinics.js';
import { ServerSidePagingTable } from "../components/Ui/Table.jsx";
import { Alert } from "../components/Ui/Alert.jsx";
import styled from 'styled-components';
import { formatDateToDDMMYYYY } from '../components/Helpers/dateFns.js';
import OpticiansClinic from '../components/Ui/Clinic/OpticiansClinic.jsx';
import RaiseProcessEyeTicket from '../components/Ui/Clinic/ManagedClinic/RaiseProcessEyeTicket.jsx';

const ButtonGroup = styled.div`
  display: flex;
  width: 100%;
`;

const Button = styled.button`
  padding: 5px 10px;
  background-color: #007bff;
  color: white;
  font-size: 11px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin: 0 1px;

  &:hover {
    background-color: #0056b3;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;


const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  margin: 0 0 20px 0;
  font-size:15px;
`;

export default function OpticianClinics({ colour }) {
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [clinics, setClinics] = useState([]);
  const [clinicCount, setClinicCount] = useState(0);
  const [showClinic, setShowClinic] = useState(false);
  const [currentClinic, setCurrentClinic] = useState(null);

  const [managedClinics, setManagedClinics] = useState([]);
  const [managedClinicsCount, setManagedClinicsCount] = useState(0);
  const [showManagedClinic, setShowManagedClinic] = useState(false);
  const [currentManagedClinic, setCurrentManagedClinic] = useState(null);

  const [pagingTracker, setPagingTracker] = useState(
    {
      currentPage: 1,
      pageSize: 10,
      filterStatus: null,
      searchValue: null,
      account: null,
      coWorker: null
    });

  const [managedClinicsPagingTracker, setManagedClinicsPagingTracker] = useState(
    {
      currentPage: 1,
      pageSize: 10,
      filterStatus: null,
      searchValue: null,
      account: null,
      coWorker: null
    });

    const loadManagedClinics = useCallback(async (account = null, page, pageSize, filterStatus = null, searchValue = "", coWorker = null) => {
      setIsLoading(true);
  
      try {
        const fetchedClinics = await fetchManagedClinics(page, pageSize);
  
        if (!fetchedClinics.ManagedClinics.length) {
          setManagedClinics([]);
          setManagedClinicsCount(0);
        }
  
        if (fetchedClinics && fetchedClinics.ManagedClinics) {
          const restructuredClinics = fetchedClinics.ManagedClinics.map(clinic => ({
            ...clinic,
          }));
  
          setManagedClinics(restructuredClinics);
          setManagedClinicsCount(fetchedClinics.Count);
        }
  
        setError(null);
      } catch (e) {
        setError(e);
        setManagedClinics([]);
          setManagedClinicsCount(0);
      } finally {
        setIsLoading(false);
      }
    }, []);

  const loadClinics = useCallback(async (account = null, page, pageSize, filterStatus = null, searchValue = "", coWorker = null) => {
    setIsLoading(true);

    try {
      const fetchedClinics = await fetchOpticiansClinics(page, pageSize);

      if (!fetchedClinics.ClinicsSummary[0].ClinicAppointments.length) {
        setClinics([]);
        setClinicCount(0);
      }

      if (fetchedClinics && fetchedClinics.ClinicsSummary[0].ClinicAppointments) {
        const restructuredClinics = fetchedClinics.ClinicsSummary[0].ClinicAppointments.map(clinic => ({
          ...clinic,
        }));

        setClinics(restructuredClinics);
        setClinicCount(fetchedClinics.ClinicsSummary[0].Count);
      }

      setError(null);
    } catch (e) {
      setError(e);
      setClinics([]);
        setClinicCount(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const managedClinicColumns = [
    {
      Header: "Company Name",
      accessor: 'CompanyName',
    },
    {
      Header: "Authorising Person",
      accessor: 'AuthorisingPerson',
    },
    {
      Header: "Account",
      accessor: 'EndUserAccountID',
    },
    {
      width: 300,
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ButtonGroup>
          <Button onClick={() => {
            setShowManagedClinic(true);
            setCurrentManagedClinic(row.original);
          }}>
            Raise Order
          </Button>
        </ButtonGroup>
      )
    },
  ];


  const columns = [
    {
      Header: "Clinic Date",
      accessor: row => formatDateToDDMMYYYY(row.dDateOfClinic),
    },
    {
      Header: "Location",
      accessor: 'cLocation',
    },
    {
      Header: "Optician Dispenser",
      accessor: 'cOpticianDispenser',
    },
    {
      Header: "Start Time",
      accessor: row => (row.ltStartTime.endsWith(":00") ? row.ltStartTime.slice(0, -3) : row.ltStartTime),
    },
    {
      Header: "End Time",
      accessor: row => (row.ltEndTime.endsWith(":00") ? row.ltEndTime.slice(0, -3) : row.ltEndTime),
    },
    {
      width: 300,
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ButtonGroup>
          <Button onClick={() => {
            setCurrentClinic(row.original);
            setShowClinic(true);
          }}>
            View Clinic
          </Button>
        </ButtonGroup>

      )
    },
  ];

  function ShowHideClinic(refresh = false) {
    setShowClinic(!showClinic);
  }

  function ShowHideEyeTicket(refresh = false) {
    setShowManagedClinic(!showManagedClinic);
  }

  return (
    <>
      {showClinic &&
        <OpticiansClinic
          showHideModal={ShowHideClinic}
          isModalOpen={showClinic}
          colours={colour}
          clinic={currentClinic} />}

      {showManagedClinic &&
        <RaiseProcessEyeTicket
          showHideModal={ShowHideEyeTicket}
          isModalOpen={showManagedClinic}
          colours={colour}
          managedClinic={currentManagedClinic}
        />
      }
      <CenterContainer>
        <FormContainer>
          <TabbedCard
            label={'Clinics'}
            headercolour={colour.sidebar}
            headerTextColour={colour.font}
            tabController={
              <TabController colours={colour}>
                <TabPanel label='My Clinics' visible={true}>
                  <TabContent>
                    <Heading>My Clinics</Heading>
                    <Paragraph>Use the table below to view and manage your Clinics.</Paragraph>
                    <ServerSidePagingTable
                      columns={columns}
                      pagingFn={loadClinics}
                      data={clinics}
                      recordCount={clinicCount}
                      setPagingTracker={setPagingTracker}
                      showSearch={false}
                    >
                    </ServerSidePagingTable>
                    {error && !isLoading && <Alert type="error">Unable to render page due to <b>{error.message}</b></Alert>}
                    {!clinicCount && <Alert type="information">No Clinics found.</Alert>}
                  </TabContent>
                </TabPanel>
                <TabPanel label='Managed Clinics' visible={true}>
                  <TabContent>
                    <Heading>Managed Clinics</Heading>
                    <Paragraph>Use the table below to view and raise orders on behalf of your Managed Clinics.</Paragraph>
                    <ServerSidePagingTable
                      columns={managedClinicColumns}
                      pagingFn={loadManagedClinics}
                      data={managedClinics}
                      recordCount={managedClinicsCount}
                      setPagingTracker={setManagedClinicsPagingTracker}
                      showSearch={false}
                    >
                    </ServerSidePagingTable>
                    {!clinicCount && <Alert type="information">No Managed Clinics found.</Alert>}
                  </TabContent>
                </TabPanel>
              </TabController>
            }
          >
          </TabbedCard>
        </FormContainer>
      </CenterContainer>
    </>
  );
}
